/**
 * BUSINESS LAYER (BL): ESQUIVADOR (SURFER - DINÁMICA ID 51)
 * Capa de lógica de negocio, validaciones defensivas, normalización de URLs multimedia,
 * motor de precarga de capas gráficas en memoria RAM y orquestación de control para Esquivador.
 * 
 * @module utilities/BL/Surfer
 */

import {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartMatchDL,
    PauseMatchDL,
    ResumeMatchDL,
    ResetMatchDL,
    FinishEarlyDL,
    GetMatchStateDL,
    SelectPlayerSideDL,
    SendPlayerActionDL,
    ReportHitDL,
    UpdateScoreDL
} from '../DL/Surfer';

// =========================================================================
// NORMALIZACIÓN DE URLs Y PRECARGA EN MEMORIA RAM
// =========================================================================

/**
 * Normaliza y resuelve URLs relativas contra la URL base de la API, eliminando prefijos `/api/`.
 * @param {string|null} rawUrl - URL relativa o absoluta del recurso.
 * @returns {string} URL absoluta normalizada y accesible.
 */
export const ResolveMediaUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return '';
    const trimmed = rawUrl.trim();
    if (!trimmed || /^(javascript|vbscript):/i.test(trimmed)) return '';
    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('//')) {
        return trimmed;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const hostBase = apiBase.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
    const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${hostBase}${cleanPath}`;
};

export const getResolvedMediaUrl = ResolveMediaUrl;

/**
 * Descarga y precarga de forma concurrente un conjunto de URLs en memoria RAM del navegador.
 * Evita flickering o retrasos de renderizado en pantallas gigantes o de afición.
 * 
 * @param {Array<string>} [urls=[]] - Lista de URLs a precargar.
 * @returns {Promise<{total: number, loaded: number, failed: number, results: Array<{url: string, success: boolean}>}>}
 */
export const PreloadImages = async (urls = []) => {
    if (!urls || !Array.isArray(urls)) {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const validUrls = Array.from(new Set(
        urls
            .filter((url) => typeof url === 'string' && url.trim().length > 0)
            .map((url) => ResolveMediaUrl(url))
            .filter((url) => url && !url.startsWith('data:') && !url.startsWith('blob:'))
    ));

    if (validUrls.length === 0) {
        return { total: 0, loaded: 0, failed: 0, results: [] };
    }

    const preloadPromises = validUrls.map((imageUrl) => {
        return new Promise((resolve) => {
            let settled = false;
            const img = new Image();

            const onSettled = (success) => {
                if (settled) return;
                settled = true;
                if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
                img.onload = null;
                img.onerror = null;
                img.onabort = null;
                resolve({ url: imageUrl, success: Boolean(success) });
            };

            const safetyTimeoutId = setTimeout(() => {
                onSettled(false);
            }, 8000);

            img.onload = () => onSettled(true);
            img.onerror = () => onSettled(false);
            img.onabort = () => onSettled(false);
            img.src = imageUrl;
        });
    });

    const results = await Promise.all(preloadPromises);
    const loaded = results.filter((r) => r.success).length;
    return {
        total: results.length,
        loaded,
        failed: results.length - loaded,
        results
    };
};

/**
 * Precarga en memoria RAM todos los recursos visuales de las capas de un tema de Esquivador.
 * @param {Object} theme - Objeto del tema con las propiedades de URLs.
 * @returns {Promise<{total: number, loaded: number, failed: number}>}
 */
export const PreloadThemeImages = async (theme) => {
    if (!theme || typeof theme !== 'object') {
        return { total: 0, loaded: 0, failed: 0 };
    }

    const assetUrls = [
        theme.brandLogoUrl,
        theme.backgroundUrl,
        theme.roadUrl,
        theme.sidePropUrl,
        theme.obstacleUrl,
        theme.coinUrl,
        theme.bonusUrl,
        theme.player1Url,
        theme.player2Url,
        theme.cockpitUrl
    ].filter(Boolean);

    return await PreloadImages(assetUrls);
};

// =========================================================================
// GESTIÓN DE TEMAS VISUALES
// =========================================================================

/**
 * Obtiene la lista de temas visuales para la sala u operador.
 * @param {string|null} [roomId=null] - GUID de sala.
 * @param {string|null} [operatorId=null] - GUID de operador.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>}
 */
export const GetThemes = async (roomId = null, operatorId = null) => {
    const result = await GetThemesDL(roomId, operatorId);
    return {
        success: Boolean(result.success),
        data: Array.isArray(result.data) ? result.data : [],
        message: result.message
    };
};

/**
 * Obtiene el detalle de un tema específico por su identificador.
 * @param {string|number} id - GUID o identificador del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetThemeById = async (id) => {
    if (!id) {
        return { success: false, message: 'El ID del tema es obligatorio.' };
    }
    return await GetThemeByIdDL(id);
};

/**
 * Valida y registra un nuevo tema visual para Esquivador.
 * @param {Object} themeData - Datos del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CreateTheme = async (themeData) => {
    if (!themeData || typeof themeData !== 'object') {
        return { success: false, message: 'Los datos del tema son obligatorios.' };
    }

    const cleanName = (themeData.name || '').trim();
    if (!cleanName) {
        return { success: false, message: 'El nombre del tema no puede estar vacío.' };
    }

    const payload = {
        name: cleanName,
        roomId: themeData.roomId || null,
        operatorId: themeData.operatorId || null,
        brandLogoUrl: themeData.brandLogoUrl || null,
        backgroundUrl: themeData.backgroundUrl || null,
        roadUrl: themeData.roadUrl || null,
        sidePropUrl: themeData.sidePropUrl || null,
        obstacleUrl: themeData.obstacleUrl || null,
        coinUrl: themeData.coinUrl || null,
        bonusUrl: themeData.bonusUrl || null,
        player1Url: themeData.player1Url || null,
        player2Url: themeData.player2Url || null,
        cockpitUrl: themeData.cockpitUrl || null,
        primaryColor: themeData.primaryColor || '#00FFCC',
        secondaryColor: themeData.secondaryColor || '#FF007F'
    };

    return await CreateThemeDL(payload);
};

/**
 * Valida y actualiza un tema visual existente.
 * @param {string|number} id - GUID o identificador del tema.
 * @param {Object} themeData - Datos actualizados del tema.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const UpdateTheme = async (id, themeData) => {
    if (!id) {
        return { success: false, message: 'El ID del tema es obligatorio para actualización.' };
    }
    if (!themeData || typeof themeData !== 'object') {
        return { success: false, message: 'Los datos de actualización son obligatorios.' };
    }

    const cleanName = (themeData.name || '').trim();
    if (!cleanName) {
        return { success: false, message: 'El nombre del tema no puede estar vacío.' };
    }

    const payload = {
        name: cleanName,
        roomId: themeData.roomId || null,
        operatorId: themeData.operatorId || null,
        brandLogoUrl: themeData.brandLogoUrl || null,
        backgroundUrl: themeData.backgroundUrl || null,
        roadUrl: themeData.roadUrl || null,
        sidePropUrl: themeData.sidePropUrl || null,
        obstacleUrl: themeData.obstacleUrl || null,
        coinUrl: themeData.coinUrl || null,
        bonusUrl: themeData.bonusUrl || null,
        player1Url: themeData.player1Url || null,
        player2Url: themeData.player2Url || null,
        cockpitUrl: themeData.cockpitUrl || null,
        primaryColor: themeData.primaryColor || '#00FFCC',
        secondaryColor: themeData.secondaryColor || '#FF007F'
    };

    return await UpdateThemeDL(id, payload);
};

/**
 * Elimina un tema visual personalizado.
 * @param {string|number} id - GUID o identificador del tema.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DeleteTheme = async (id) => {
    if (!id) {
        return { success: false, message: 'El ID del tema es obligatorio.' };
    }
    return await DeleteThemeDL(id);
};

/**
 * Clona un tema existente asignándole un nombre sugerido o personalizado.
 * @param {string|number} id - GUID o identificador del tema origen.
 * @param {string|null} [newName=null] - Nombre para el tema clonado.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CloneTheme = async (id, newName = null) => {
    if (!id) {
        return { success: false, message: 'El ID del tema a clonar es obligatorio.' };
    }
    return await CloneThemeDL(id, newName);
};

// =========================================================================
// CONFIGURACIÓN TÉCNICA DE SALA
// =========================================================================

/**
 * Obtiene y sanitiza la configuración activa de Esquivador para una sala.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetConfig = async (roomId) => {
    if (!roomId) {
        return { success: false, message: 'El RoomId es obligatorio.' };
    }
    return await GetConfigDL(roomId);
};

/**
 * Valida y persiste la configuración de Esquivador para una sala.
 * @param {Object} configData - Parámetros de simulación y reglas.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SaveConfig = async (configData) => {
    if (!configData || !configData.roomId) {
        return { success: false, message: 'El RoomId es obligatorio para guardar la configuración.' };
    }

    const payload = {
        roomId: configData.roomId,
        activeThemeId: configData.activeThemeId || null,
        laneCount: Number(configData.laneCount) || 3,
        cameraView: configData.cameraView || 'third_person',
        canJump: Boolean(configData.canJump),
        canSlide: Boolean(configData.canSlide),
        speedPreset: configData.speedPreset || 'Normal',
        baseSpeed: Math.max(100, Math.min(2000, Number(configData.baseSpeed) || 380)),
        maxSpeed: Math.max(100, Math.min(3000, Number(configData.maxSpeed) || 650)),
        difficultyMode: configData.difficultyMode || 'both',
        speedAcceleration: Number(configData.speedAcceleration) || 5.0,
        densityFactor: Number(configData.densityFactor) || 1.0,
        pointsPerTick: Number(configData.pointsPerTick) || 5,
        pointsPerCoin: Number(configData.pointsPerCoin) || 50,
        pointsPerBonus: Number(configData.pointsPerBonus) || 500,
        penaltyPerHit: Number(configData.penaltyPerHit) || 150,
        winMode: configData.winMode || 'points_deduction',
        endCondition: configData.endCondition || 'time',
        timeLimitSeconds: Math.max(10, Math.min(600, Number(configData.timeLimitSeconds) || 45)),
        targetPointsGoal: Number(configData.targetPointsGoal) || 3000,
        invulnerabilityDuration: Number(configData.invulnerabilityDuration) || 1.5
    };

    return await SaveConfigDL(payload);
};

// =========================================================================
// HISTORIAL DE PARTIDAS
// =========================================================================

/**
 * Obtiene el historial de duelos disputados en una sala.
 * @param {string} roomId - GUID de la sala.
 * @param {number} [limit=20] - Límite de partidas.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>}
 */
export const GetMatchHistory = async (roomId, limit = 20) => {
    if (!roomId) {
        return { success: false, data: [], message: 'El RoomId es obligatorio.' };
    }
    const result = await GetMatchHistoryDL(roomId, limit);
    return {
        success: Boolean(result.success),
        data: Array.isArray(result.data) ? result.data : [],
        message: result.message
    };
};

// =========================================================================
// SUBIDA MULTIMEDIA (SKIASHARP)
// =========================================================================

/**
 * Valida y procesa la subida de un asset multimedia individual.
 * @param {File} file - Archivo de imagen a subir.
 * @param {string} [assetType=''] - Tipo de asset (background, road, obstacle, coin, bonus, player1, player2, cockpit, brandLogo).
 * @returns {Promise<{success: boolean, url?: string, message?: string}>}
 */
export const UploadAsset = async (file, assetType = '') => {
    if (!file) {
        return { success: false, message: 'Debe seleccionar un archivo válido.' };
    }
    const maxSizeBytes = 20 * 1024 * 1024; // 20 MB
    if (file.size > maxSizeBytes) {
        return { success: false, message: 'El archivo excede el tamaño máximo permitido de 20 MB.' };
    }
    return await UploadAssetDL(file, assetType);
};

/**
 * Valida y procesa la subida de un lote multimedia.
 * @param {Array<File>|FileList} files - Lista de archivos.
 * @param {string} [assetType=''] - Tipo de recurso.
 * @returns {Promise<{success: boolean, urls?: Array<string>, message?: string}>}
 */
export const UploadBatchMedia = async (files, assetType = '') => {
    if (!files || files.length === 0) {
        return { success: false, message: 'Debe seleccionar al menos un archivo.' };
    }
    return await UploadBatchMediaDL(files, assetType);
};

// =========================================================================
// CONTROL DE PARTIDAS EN TIEMPO REAL
// =========================================================================

/**
 * Proyecta la portada de Esquivador en la sala.
 * @param {string} roomId - GUID de la sala.
 * @param {string|null} [themeId=null] - GUID opcional del tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ShowCover = async (roomId, themeId = null) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await ShowCoverDL(roomId, themeId);
};

/**
 * Proyecta la escena de juego 1 vs 1 y carga los recursos.
 * @param {string} roomId - GUID de la sala.
 * @param {string|null} [themeId=null] - GUID opcional del tema visual.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ShowGame = async (roomId, themeId = null) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await ShowGameSceneDL(roomId, themeId);
};

/**
 * Inicia la cuenta regresiva oficial y la carrera de Esquivador.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const StartGame = async (roomId) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await StartMatchDL(roomId);
};

/**
 * Reinicia la carrera de Esquivador devolviéndola al estado listo para jugar.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const ResetGame = async (roomId) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await ResetMatchDL(roomId);
};

/**
 * Finaliza anticipadamente la carrera de Esquivador y proclama ganadores.
 * @param {string} roomId - GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const FinishEarly = async (roomId) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await FinishEarlyDL(roomId);
};

export const GetRealtimeState = async (roomId) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await GetMatchStateDL(roomId);
};

export const PauseMatch = async (roomId) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await PauseMatchDL(roomId);
};

export const ResumeMatch = async (roomId) => {
    if (!roomId) return { success: false, message: 'El RoomId es obligatorio.' };
    return await ResumeMatchDL(roomId);
};

export const SaveMatchResult = async (matchData) => {
    if (!matchData) return { success: false, message: 'Los datos de la partida son obligatorios.' };
    return await SaveMatchResultDL(matchData);
};

export const SelectPlayerSide = async (roomId, connectionId, side, playerName) => {
    if (!roomId || !side) return { success: false, message: 'El RoomId y el bando son obligatorios.' };
    return await SelectPlayerSideDL(roomId, connectionId, side, playerName);
};

export const SendPlayerAction = async (roomId, connectionId, side, action) => {
    if (!roomId || !side || !action) return { success: false, message: 'Parámetros incompletos para la acción.' };
    return await SendPlayerActionDL(roomId, connectionId, side, action);
};

export const ReportHit = async (roomId, side) => {
    if (!roomId || !side) return { success: false, message: 'El RoomId y el bando son obligatorios.' };
    return await ReportHitDL(roomId, side);
};

export const UpdateScore = async (roomId, side, pointsDelta, reason = null) => {
    if (!roomId || !side) return { success: false, message: 'El RoomId y el bando son obligatorios.' };
    return await UpdateScoreDL(roomId, side, pointsDelta, reason);
};

const SurferBL = {
    ResolveMediaUrl,
    getResolvedMediaUrl,
    PreloadImages,
    PreloadThemeImages,
    GetThemes,
    GetThemeById,
    CreateTheme,
    UpdateTheme,
    DeleteTheme,
    CloneTheme,
    GetConfig,
    SaveConfig,
    GetMatchHistory,
    SaveMatchResult,
    UploadAsset,
    UploadBatchMedia,
    ShowCover,
    ShowGame,
    StartGame,
    PauseMatch,
    ResumeMatch,
    ResetGame,
    FinishEarly,
    GetRealtimeState,
    SelectPlayerSide,
    SendPlayerAction,
    ReportHit,
    UpdateScore
};

export default SurferBL;
